import { useWorkspaceText } from '../interface-copy';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useNamespaceCopy } from '@/i18n/ns';
import runtimeCopy from '@/i18n/ns/workspace-knowledge-runtime';
import editorCopy from '@/i18n/ns/workspace-editors';
import { formatCopy } from '@/i18n/ns';
import { forwardRef, type ReactNode } from 'react';
import { isWorkspaceLink } from '@/lib/workspace-links';
import { WorkspaceIcon } from '../icons';
import { WorkspaceMarkdown } from '../markdown';
import { relativeAge, suggestionAuthor } from './format';
import type { BlockPart } from './marks';
import type { PageSuggestion } from './types';

export type SuggestionDecision = 'accept' | 'reject' | 'withdraw';

/** What a suggestion that is not a text edit asks for, in one line. */
function kindLine(
  suggestion: PageSuggestion,
  titleOf: (id: string | null) => string,
  copy: Readonly<Record<keyof typeof editorCopy.en, string>>,
) {
  if (suggestion.kind === 'create_page')
    return formatCopy(copy.suggestionNewPageUnder, {
      title: suggestion.title,
      parent: titleOf(suggestion.parentId),
    });
  if (suggestion.kind === 'move_page')
    return formatCopy(copy.suggestionMoveUnder, { parent: titleOf(suggestion.parentId) });
  if (suggestion.kind === 'archive_page') return copy.suggestionArchive;
  if (suggestion.rename) return formatCopy(copy.suggestionRenameTo, { title: suggestion.rename });
  return null;
}

function SourceLink({ suggestion }: { suggestion: PageSuggestion }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const source = suggestion.source;
  if (!source?.url || !isWorkspaceLink(source.url)) return null;
  return (
    <a className="ws-suggestion-source" href={source.url}>
      {source.kind === 'meeting' ? uiText('Open the meeting') : uiText('Open the chat')}
      <WorkspaceIcon name="arrow" size={12} />
    </a>
  );
}

function Actions({
  suggestion,
  canPublish,
  canAccept,
  busy,
  archived,
  onDecide,
}: {
  suggestion: PageSuggestion;
  canPublish: boolean;
  /** False when the change cannot be shown, so it cannot be accepted unseen. */
  canAccept: boolean;
  busy: boolean;
  archived: boolean;
  onDecide: (decision: SuggestionDecision) => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const open = suggestion.state === 'open';
  if (canPublish)
    return (
      <div className="ws-suggestion-actions">
        {open && canAccept && (
          <button
            type="button"
            className="ws-button primary"
            disabled={busy}
            onClick={() => onDecide('accept')}
          >
            <WorkspaceIcon name="check" size={14} />
            {archived && suggestion.kind !== 'archive_page'
              ? uiText('Accept and restore')
              : uiText('Accept')}
          </button>
        )}
        <button
          type="button"
          className="ws-button"
          disabled={busy}
          onClick={() => onDecide('reject')}
        >
          {uiText('Reject')}
        </button>
      </div>
    );
  if (suggestion.isMine)
    return (
      <div className="ws-suggestion-actions ws-suggestion-waiting">
        <span>{uiText('Your suggestion · waiting for a publisher')}</span>
        <button
          type="button"
          className="ws-text-button"
          disabled={busy}
          onClick={() => onDecide('withdraw')}
        >
          {uiText('Withdraw')}
        </button>
      </div>
    );
  return <p className="ws-suggestion-waiting">{uiText('Waiting for a publisher')}</p>;
}

/** A change the page cannot mark, as the blocks it removes and adds. */
function ChangePreview({ parts }: { parts: BlockPart[] }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  return (
    <div className="ws-suggestion-changes">
      {parts.map((part, position) => (
        <div
          key={position}
          className={`ws-suggest-block ws-suggest-block-${part.kind === 'remove' ? 'del' : 'ins'}`}
        >
          <span className="ws-sr-only">
            {part.kind === 'remove' ? uiText('Removed:') : uiText('Added:')}
          </span>
          <WorkspaceMarkdown text={part.text} />
        </div>
      ))}
    </div>
  );
}

/**
 * One open suggestion: who, when, why, where it came from (only when the
 * viewer may open it) and the decision this viewer can make. Its header
 * highlights the suggestion's marks in the page. A change the page cannot
 * mark is shown on the card instead, or, when it cannot be shown at all,
 * cannot be accepted.
 */
export const SuggestionCard = forwardRef<
  HTMLButtonElement,
  {
    suggestion: PageSuggestion;
    number: number;
    canPublish: boolean;
    focused: boolean;
    busy: boolean;
    archived: boolean;
    /** Why its changes are not marked in the text: they overlap another's, or do not fit it. */
    unmarked: 'overlap' | 'mismatch' | null;
    /** The change of an unmarked ('overlap') suggestion, shown on the card. */
    preview: BlockPart[] | null;
    titleOf: (id: string | null) => string;
    onFocus: () => void;
    onDecide: (decision: SuggestionDecision) => void;
    /** Extra content under the card (the phone sheet's pager). */
    footer?: ReactNode;
  }
>(function SuggestionCard(
  {
    suggestion,
    number,
    canPublish,
    focused,
    busy,
    archived,
    unmarked,
    preview,
    titleOf,
    onFocus,
    onDecide,
    footer,
  },
  header,
) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const locale = useChatInterfaceLocale();
  const copy = useNamespaceCopy(runtimeCopy);
  const editor = useNamespaceCopy(editorCopy);
  const author = suggestionAuthor(suggestion, copy);
  const age = relativeAge(suggestion.createdAt, locale, Date.now(), copy);
  const line = kindLine(suggestion, titleOf, editor);
  const marked = suggestion.kind === 'update_page' && suggestion.changes.length > 0 && !unmarked;
  const note =
    unmarked === 'overlap'
      ? editor.suggestionOverlapNote
      : unmarked === 'mismatch'
        ? editor.suggestionMismatchNote
        : null;
  const shown = unmarked !== 'overlap' || Boolean(preview?.length);
  // Accepting brings back an archived page only through an edit to its text;
  // a move, or a new page under it, needs the page restored first.
  const needsRestore =
    archived && (suggestion.kind === 'move_page' || suggestion.kind === 'create_page');
  return (
    <article
      className="ws-suggestion-card"
      data-suggestion={suggestion.id}
      data-focused={focused ? 'true' : undefined}
      data-state={suggestion.state}
      aria-label={uiText('Suggestion {value1}', { value1: number })}
    >
      <button
        ref={header}
        type="button"
        className="ws-suggestion-who"
        aria-pressed={focused}
        aria-label={uiText('Suggestion {value1} from {value2}{value3}', {
          value1: number,
          value2: author.name,
          value3: marked ? `: ${editor.showChange}` : '',
        })}
        onClick={onFocus}
      >
        <span className="ws-suggestion-number" aria-hidden="true">
          {number}
        </span>
        <span className="ws-suggestion-author">
          <strong>{author.name}</strong>
          <small>{[author.detail, age].filter(Boolean).join(' · ')}</small>
        </span>
      </button>
      {line && <p className="ws-suggestion-kind">{line}</p>}
      {suggestion.kind === 'create_page' && suggestion.markdown && (
        <details className="ws-suggestion-preview">
          <summary>{uiText('Read the suggested page')}</summary>
          <WorkspaceMarkdown text={suggestion.markdown} />
        </details>
      )}
      {suggestion.reason && <p className="ws-suggestion-reason">{suggestion.reason}</p>}
      <SourceLink suggestion={suggestion} />
      {suggestion.state === 'outdated' && (
        <p className="ws-suggestion-outdated">
          {uiText(
            'Outdated: the text it changes was edited after it was suggested. It can be rejected or withdrawn; a new suggestion can be made against the current text.',
          )}
        </p>
      )}
      {note && <p className="ws-suggestion-note">{note}</p>}
      {needsRestore && suggestion.state === 'open' && canPublish && (
        <p className="ws-suggestion-note">
          {uiText('Restore this page first to accept this suggestion.')}
        </p>
      )}
      {unmarked === 'overlap' && preview && preview.length > 0 && <ChangePreview parts={preview} />}
      <Actions
        suggestion={suggestion}
        canPublish={canPublish}
        canAccept={unmarked !== 'mismatch' && shown && !needsRestore}
        busy={busy}
        archived={archived}
        onDecide={onDecide}
      />
      {footer}
    </article>
  );
});
